import { Controller } from "@hotwired/stimulus"
import debounce from "lodash.debounce"

export default class extends Controller {
  static get targets() { return ["form", "status", "statusText"] }
  static get values() {
    return { url: String, savedAt: String }
  }

  initialize() {
    this.save = debounce(this.save.bind(this), 2000)
  }

  connect() {
    this.dirty = false
    this.submitting = false
    this.announced = false

    this.onInput = this.onInput.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onPageHide = this.onPageHide.bind(this)
    this.onVisibilityChange = this.onVisibilityChange.bind(this)

    this.formTarget.addEventListener("input", this.onInput)
    this.formTarget.addEventListener("change", this.onInput)
    this.formTarget.addEventListener("trix-change", this.onInput)
    this.formTarget.addEventListener("blur", this.onBlur, true)
    this.formTarget.addEventListener("submit", this.onSubmit)
    window.addEventListener("pagehide", this.onPageHide)
    document.addEventListener("visibilitychange", this.onVisibilityChange)

    // Select2 dispatches jQuery events that native listeners never see, and the
    // existing select2 controller only binds select2:select — so a *removed*
    // content warning would otherwise never trigger a save.
    if (window.$) {
      this.$selects = window.$(this.formTarget).find("select.select2-control")
      this.$selects.on("change select2:select select2:unselect", this.onInput)
    }

    if (this.hasSavedAtValue && this.savedAtValue) {
      this.announced = true
      this.showSaved(new Date(this.savedAtValue))
    }
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("input", this.onInput)
      this.formTarget.removeEventListener("change", this.onInput)
      this.formTarget.removeEventListener("trix-change", this.onInput)
      this.formTarget.removeEventListener("blur", this.onBlur, true)
      this.formTarget.removeEventListener("submit", this.onSubmit)
    }
    window.removeEventListener("pagehide", this.onPageHide)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    if (this.$selects) this.$selects.off("change select2:select select2:unselect", this.onInput)
    if (this.save.cancel) this.save.cancel()
  }

  onInput() {
    if (this.submitting) return
    this.dirty = true
    this.save()
  }

  onBlur(event) {
    const to = event.relatedTarget
    if (to && this.formTarget.contains(to) && (to.type === "submit" || to.tagName === "BUTTON")) return
    if (this.dirty) this.flush()
  }

  onSubmit() {
    this.submitting = true
    if (this.save.cancel) this.save.cancel()
  }

  onPageHide() {
    if (this.dirty && !this.submitting) this.beacon()
  }

  onVisibilityChange() {
    if (document.visibilityState === "hidden" && this.dirty && !this.submitting) this.beacon()
  }

  flush() {
    if (this.save.flush) this.save.flush()
  }

  formData() {
    const data = new FormData(this.formTarget)
    data.delete("authenticity_token")
    data.append("authenticity_token", this.csrfToken())
    return data
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  beacon() {
    if (!navigator.sendBeacon) return
    this.dirty = false
    navigator.sendBeacon(this.urlValue, this.formData())
  }

  save() {
    if (this.submitting) return

    this.dirty = false
    if (!this.announced) this.showSaving()

    fetch(this.urlValue, {
      method: "POST",
      body: this.formData(),
      headers: { "X-CSRF-Token": this.csrfToken(), "Accept": "application/json" },
      credentials: "same-origin"
    })
      .then(response => {
        if (!response.ok) throw new Error(response.status)
        return response.json()
      })
      .then(body => {
        if (body.status === "discarded") return this.hideStatus()
        this.announced = true
        this.showSaved(new Date(body.saved_at))
      })
      .catch(() => this.showError())
  }

  showSaving() {
    this.setStatus("Autosaving…", false)
  }

  showSaved(savedAt) {
    this.setStatus(`Last saved: ${this.relativeTime(savedAt)}`, false)
  }

  showError() {
    this.setStatus("Couldn't save your draft — we'll keep trying", true)
  }

  hideStatus() {
    this.statusTargets.forEach(el => el.classList.add("hidden"))
  }

  setStatus(text, isError) {
    this.statusTextTargets.forEach(el => { el.textContent = text })
    this.statusTargets.forEach(el => {
      el.classList.remove("hidden")
      el.classList.toggle("text-red-700", isError)
      el.classList.toggle("text-darkerGrey", !isError)
      el.classList.toggle("dark:text-midGrey", !isError)
    })
  }

  relativeTime(date) {
    const seconds = Math.round((Date.now() - date.getTime()) / 1000)
    if (isNaN(seconds) || seconds < 45) return "just now"
    const minutes = Math.round(seconds / 60)
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`
    const days = Math.round(hours / 24)
    return `${days} day${days === 1 ? "" : "s"} ago`
  }
}
